/**
 * Session Management Service
 *
 * Manages user session timeout, inactivity tracking, and token refresh monitoring.
 * Firebase SDK handles automatic token refresh, but we need to add:
 * - Inactivity timeout
 * - Logout on token refresh failure
 *
 * Firebase Auth automatically:
 * - Refreshes ID tokens (1 hour expiry) using refresh tokens
 * - Persists session to localStorage
 * - Restores session on page load
 */

import { firebaseAuthService } from '@/shared/services/auth/firebaseAuthService';
import { UserProfile } from '@/shared/types';
import { BehaviorSubject } from 'rxjs';

// Default session timeout: 30 minutes of inactivity
const DEFAULT_SESSION_TIMEOUT_MS = 30 * 60 * 1000;

// Token refresh check interval: every 5 minutes
const TOKEN_CHECK_INTERVAL_MS = 5 * 60 * 1000;

interface SessionState {
  isAuthenticated: boolean;
  lastActivity: number;
  sessionTimeout: number;
}

class SessionService {
  private sessionState: SessionState = {
    isAuthenticated: false,
    lastActivity: Date.now(),
    sessionTimeout: DEFAULT_SESSION_TIMEOUT_MS,
  };

  private inactivityTimer: ReturnType<typeof setTimeout> | null = null;
  private tokenCheckTimer: ReturnType<typeof setInterval> | null = null;
  private sessionStateSubject = new BehaviorSubject<SessionState>(this.sessionState);

  constructor() {
    this.setupActivityListeners();
    this.loadSessionSettings();
  }

  /**
   * Load session timeout setting from localStorage
   */
  private loadSessionSettings(): void {
    try {
      const savedTimeout = localStorage.getItem('sessionTimeout');
      if (savedTimeout) {
        this.sessionState.sessionTimeout = parseInt(savedTimeout, 10);
      }
    } catch (error) {
      console.warn('Could not load session settings:', error);
    }
  }

  /**
   * Save session timeout setting to localStorage
   */
  private saveSessionSettings(): void {
    try {
      localStorage.setItem('sessionTimeout', this.sessionState.sessionTimeout.toString());
    } catch (error) {
      console.warn('Could not save session settings:', error);
    }
  }

  /**
   * Set session timeout (in milliseconds)
   */
  setSessionTimeout(timeoutMs: number): void {
    this.sessionState.sessionTimeout = timeoutMs;
    this.saveSessionSettings();
    this.resetInactivityTimer();
    this.notifyStateChange();
  }

  /**
   * Get current session timeout
   */
  getSessionTimeout(): number {
    return this.sessionState.sessionTimeout;
  }

  /**
   * Get session state as observable
   */
  getSessionState() {
    return this.sessionStateSubject.asObservable();
  }

  /**
   * Get current session state (snapshot)
   */
  getCurrentState(): SessionState {
    return { ...this.sessionState };
  }

  /**
   * Get remaining time until session timeout (in milliseconds)
   */
  getRemainingTime(): number {
    const elapsed = Date.now() - this.sessionState.lastActivity;
    return Math.max(0, this.sessionState.sessionTimeout - elapsed);
  }

  /**
   * Start session monitoring for authenticated user
   */
  async startSession(user: UserProfile): Promise<void> {
    this.sessionState.isAuthenticated = true;
    this.sessionState.lastActivity = Date.now();
    this.notifyStateChange();

    this.resetInactivityTimer();
    this.startTokenRefreshMonitoring();

    console.log('✅ Session started for user:', user.email);
  }

  /**
   * End session (logout)
   */
  async endSession(): Promise<void> {
    this.sessionState.isAuthenticated = false;
    this.sessionState.lastActivity = Date.now();
    this.notifyStateChange();

    this.clearTimers();

    console.log('✅ Session ended');
  }

  /**
   * Reset inactivity timer
   */
  private resetInactivityTimer(): void {
    this.clearTimers();

    if (!this.sessionState.isAuthenticated) {
      return;
    }

    this.inactivityTimer = setTimeout(async () => {
      console.log('⏰ Session timeout - logging out user');
      await this.logoutDueToInactivity();
    }, this.sessionState.sessionTimeout);
  }

  /**
   * Clear all timers
   */
  private clearTimers(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }

    if (this.tokenCheckTimer) {
      clearInterval(this.tokenCheckTimer);
      this.tokenCheckTimer = null;
    }
  }

  /**
   * Setup activity listeners to track user interaction
   */
  private setupActivityListeners(): void {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    const updateActivity = () => {
      if (this.sessionState.isAuthenticated) {
        const now = Date.now();
        // Only reset if significant activity (more than 1 second since last activity)
        if (now - this.sessionState.lastActivity > 1000) {
          this.sessionState.lastActivity = now;
          this.resetInactivityTimer();
          this.notifyStateChange();
        }
      }
    };

    // Debounce activity updates to avoid excessive timer resets
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const debouncedUpdate = () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      debounceTimer = setTimeout(updateActivity, 1000);
    };

    events.forEach(event => {
      document.addEventListener(event, debouncedUpdate, { passive: true });
    });

    // Also track visibility change (user returns to tab)
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && this.sessionState.isAuthenticated) {
        updateActivity();
      }
    });
  }

  /**
   * Start monitoring token refresh for failures
   * Firebase SDK automatically refreshes tokens, but we need to detect failures
   */
  private startTokenRefreshMonitoring(): void {
    this.clearTimers();

    this.tokenCheckTimer = setInterval(async () => {
      if (!this.sessionState.isAuthenticated) {
        return;
      }

      try {
        // Try to get a fresh token - this will trigger automatic refresh if needed
        const token = await firebaseAuthService.getIdToken(true);

        if (!token) {
          console.warn('⚠️ Token refresh failed - logging out user');
          await this.logoutDueToTokenError();
        }
      } catch (error) {
        console.error('❌ Token refresh error:', error);
        await this.logoutDueToTokenError();
      }
    }, TOKEN_CHECK_INTERVAL_MS);
  }

  /**
   * Logout user due to inactivity
   */
  private async logoutDueToInactivity(): Promise<void> {
    console.log('🚪 Logging out due to inactivity');
    await this.performLogout('You have been logged out due to inactivity');
  }

  /**
   * Logout user due to token refresh failure
   */
  private async logoutDueToTokenError(): Promise<void> {
    console.log('🚪 Logging out due to token error');
    await this.performLogout('Your session has expired. Please log in again.');
  }

  /**
   * Perform logout with optional message
   */
  private async performLogout(message?: string): Promise<void> {
    try {
      await firebaseAuthService.signOut();
      await this.endSession();

      // Store logout message for display on login page
      if (message) {
        sessionStorage.setItem('logoutMessage', message);
      }

      // Redirect to login page
      window.location.href = '/auth';
    } catch (error) {
      console.error('Error during logout:', error);
      // Force redirect even if logout fails
      window.location.href = '/auth';
    }
  }

  /**
   * Notify subscribers of state change
   */
  private notifyStateChange(): void {
    this.sessionStateSubject.next({ ...this.sessionState });
  }

  /**
   * Cleanup service
   */
  destroy(): void {
    this.clearTimers();
    this.sessionStateSubject.complete();
  }
}

// Create and export singleton instance
export const sessionService = new SessionService();
